import { useQuery } from '@tanstack/react-query';

import { bankFeedApi } from 'domain/bankFeed/api';
import type { BankFeedAccountType } from 'domain/bankFeed/schemas';
import { queryOptions } from 'services/query';
import { bankFeedKeys } from './keys';

// Column definitions per accountType. BE owns the column set — there are no
// FE write paths, so this file has no mutation hooks. `staleTime: Infinity`
// because columns rarely change; a hard reload refetches if BE adds new ones.
export const bankFeedTransactionsHeadersQuery = (accountType: BankFeedAccountType) =>
  queryOptions({
    queryKey: bankFeedKeys.transactionsHeaders(accountType),
    queryFn: () => bankFeedApi.getTransactionsGridHeaders({ query: { account_type: accountType } }),
    staleTime: Infinity,
  });

export const useBankFeedTransactionsHeaders = (accountType: BankFeedAccountType) =>
  useQuery(bankFeedTransactionsHeadersQuery(accountType));

// Per-account transaction counts, available currencies and an export-in-progress flag,
// plus the company default currency BankFeedRoute seeds into `toolbarFilters.currency`
// (DA-19089). (last-synced / sync-in-progress are deferred to the sync phase — not in the
// schema yet, see schemas.ts.)
//
// cacheTime 0 is the load-bearing override: the summary is not retained across visits, so
// the next route entry always fetches fresh and the default-currency seed reads the current
// company currency. With a retained cache TanStack would serve the stale summary first and
// the apply-once seed in BankFeedRoute would lock onto it, ignoring the fresh refetch — the
// exact "changed the currency in Settings, came back, still see the old one" bug. It also
// keeps the grid (gated on a non-null currency) from mounting on a stale currency and then
// refetching once the fresh one lands. staleTime 0 is stated explicitly for intent though it
// also matches the global queryClient default.
//
// The summary has a single observer: BankFeedRoute subscribes via useBankFeedSummary and
// shares the result through BankFeedRouteContext, so FilterToolbar, ExportButton and the
// Outlet child grids read it from there instead of opening their own observer. That observer
// lives in the parent route and stays mounted across in-page tab switches, so GC fires only
// on full route leave. No mutation hooks — BE owns the values.
export const bankFeedTransactionsSummaryQuery = () =>
  queryOptions({
    queryKey: bankFeedKeys.transactionsSummary(),
    queryFn: () => bankFeedApi.getTransactionsGridSummary(),
    staleTime: 0,
    cacheTime: 0,
  });

export const useBankFeedTransactionsSummary = () => useQuery(bankFeedTransactionsSummaryQuery());
